import { QueryObserverResult, RefetchOptions, useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIMES } from '../../shared/providers/react-query-provider';
import { DossierId } from '../dossier/types';

import { useAppSelector } from '../../shared/redux';

import { APICallStatus } from '../../design-system/types';

import { useGetPhoneCalls } from '../phoning/queries';

import {
    DiscussionItem,
    DisplayDiscussion,
    Message,
    MessageAttachment,
    NormalizedDiscussion,
} from './types';
import { getDiscussion, getDiscussionAttachments } from './api';
import {
    addAttachmentLinkToMessage,
    getIsResponseRequiredState,
    groupDiscussionItemByDate,
} from './operations';
import { getDiscussion as getDiscussionSelector } from './selectors';

export const getDiscussionMessagesQueryKey = (dossierId: DossierId) => [
    'discussion',
    dossierId,
    'all',
];

export const getDiscussionAttachmentsQueryKey = (dossierId: DossierId) => [
    'discussion',
    dossierId,
    'attachments',
];

export const useGetDiscussionAttachments = (dossierId: DossierId, enabled = true) => {
    return useQuery<MessageAttachment[]>({
        queryKey: getDiscussionAttachmentsQueryKey(dossierId),
        queryFn: () => getDiscussionAttachments(dossierId),
        enabled: !!dossierId && enabled,
        staleTime: QUERY_STALE_TIMES.DISCUSSION,
    });
};

export type AllDiscussionMessagesResponse = {
    dossierId: DossierId;
    messages: Message[];
    isResponseRequired: boolean;
};

export const useAllDiscussionMessages = (dossierId: DossierId) => {
    return useQuery<AllDiscussionMessagesResponse>({
        queryKey: getDiscussionMessagesQueryKey(dossierId),
        queryFn: async () => {
            const allMessages: Record<string, Message> = {};
            let isResponseRequired = false;
            let currentPage = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const response: NormalizedDiscussion = await getDiscussion(dossierId, currentPage);
                const discussionData = response[dossierId];

                if (discussionData.messages) {
                    discussionData.messages?.forEach((message) => {
                        allMessages[message.id] = message;
                    });
                }

                hasNextPage = discussionData.pagination.links.next !== undefined;
                currentPage = discussionData.pagination.currentPage + 1;
                // eslint-disable-next-line prefer-destructuring -- reassignment inside loop, destructuring would not work here
                isResponseRequired = discussionData.isResponseRequired;
            }

            return {
                dossierId,
                messages: Object.values(allMessages),
                isResponseRequired,
            };
        },
        enabled: !!dossierId,
        staleTime: QUERY_STALE_TIMES.DISCUSSION,
    });
};

export const useLastTravellerMessage = (dossierId: DossierId): Message | null => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};

    if (!messages || messages.length === 0) {
        return null;
    }

    const travellerMessages = messages.filter((message) => message.isFromTraveller);

    if (travellerMessages.length === 0) {
        return null;
    }

    return travellerMessages[travellerMessages.length - 1];
};

export const useIsLastMessageAndFromTraveller = (
    dossierId: DossierId,
    messageId: string | number,
): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};
    if (!messages || messages.length === 0) {
        return false;
    }
    const lastMessage = messages.at(-1);

    return !!lastMessage?.isFromTraveller && lastMessage.id === messageId;
};

export const useIsLastMessageAndFromAgent = (
    dossierId: DossierId,
    messageId: string | number,
): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};
    if (!messages || messages.length === 0) {
        return false;
    }
    const lastMessage = messages.at(-1);

    return !!lastMessage?.isFromAgent && lastMessage.id === messageId;
};

export const useIsFirstAgentMessage = (
    dossierId: DossierId,
    messageId: string | number,
): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};

    if (!messages || messages.length === 0) return false;

    const agentMessages = messages
        .filter((message) => message.isFromAgent)
        .sort((a, b) => a.sendDate.getTime() - b.sendDate.getTime());

    if (agentMessages.length === 0) return false;

    return agentMessages[0].id === messageId;
};

export const useFilteredDiscussionGroupedByDate = (
    dossierId: DossierId,
): DisplayDiscussion & {
    refetch: (
        options?: RefetchOptions,
    ) => Promise<QueryObserverResult<AllDiscussionMessagesResponse, Error>>;
    status: APICallStatus;
} => {
    const { data, refetch, status } = useAllDiscussionMessages(dossierId);
    const { messages, isResponseRequired } = data || {};

    // @todo to replace after schedule and draft migration
    const discussionLegacy = useAppSelector(getDiscussionSelector(dossierId));
    const { data: phoneCalls = [] } = useGetPhoneCalls(dossierId);

    const messageWithAttachment = [...new Set(messages)].map(addAttachmentLinkToMessage);
    const messagesAndPhoneCalls: DiscussionItem[] = [...messageWithAttachment, ...phoneCalls].sort(
        (a, b) => a.sendDate.getTime() - b.sendDate.getTime(),
    );

    const discussionItems = [...new Set(messagesAndPhoneCalls)].reduce(
        groupDiscussionItemByDate,
        {},
    );

    // eslint-disable-next-line sonarjs/no-misleading-array-reverse -- result immediately consumed, mutation safe here
    const lastMessage = messageWithAttachment
        .sort((a, b) => a.sendDate.getTime() - b.sendDate.getTime())
        .at(-1);
    const isResponseRequiredState = getIsResponseRequiredState(
        lastMessage?.isFromTraveller,
        Boolean(isResponseRequired),
    );

    return {
        messages: discussionItems,
        scheduledMessage: discussionLegacy.scheduledMessage,
        sendingMessage: discussionLegacy.sendingMessage,
        isResponseRequiredState,
        refetch,
        status,
    };
};
